import React, { useEffect, useState } from 'react';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import { API_BASE } from '../../config/runtime';
const PESO_SYMBOL = '\u20B1';

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const normalizeStatus = (status) => (status || '').toString().trim().toLowerCase();

const parseDateValue = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDateKey = (value) => {
  const date = parseDateValue(value);
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const getTodayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return `${PESO_SYMBOL}0`;
  if (amount >= 1_000_000) return `${PESO_SYMBOL}${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${PESO_SYMBOL}${(amount / 1_000).toFixed(1)}K`;
  return `${PESO_SYMBOL}${amount.toLocaleString('en-PH')}`;
};

const buildMonthlySeries = (bookings, monthsBack) => {
  const now = new Date();
  const buckets = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const label = monthsBack > 6
      ? date.toLocaleString('en-US', { month: 'short', year: '2-digit' })
      : date.toLocaleString('en-US', { month: 'short' });
    buckets.push({ key, name: label, value: 0 });
  }
  const bucketMap = new Map(buckets.map((b) => [b.key, b]));
  bookings.forEach((booking) => {
    const status = normalizeStatus(booking.status);
    if (['cancelled', 'canceled', 'declined'].includes(status)) return;
    const date = parseDateValue(booking.appointment_date || booking.created_at);
    if (!date) return;
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const bucket = bucketMap.get(key);
    if (bucket) bucket.value += 1;
  });
  return buckets.map(({ key, ...rest }) => rest);
};

// Custom tooltip for the bar chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1c1310] text-white rounded-xl px-4 py-3 shadow-2xl">
        <p className="text-xs font-semibold text-[#c8a48a] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-bold">{payload[0].value} <span className="text-xs font-normal text-gray-400">bookings</span></p>
      </div>
    );
  }
  return null;
};

// Stat card component
const StatCard = ({ label, value, loading, accent, icon, sub }) => (
  <div className={`
    relative overflow-hidden rounded-2xl p-5 sm:p-6
    ${accent === 'dark'
      ? 'bg-[#1c1310] text-white'
      : accent === 'warm'
      ? 'bg-gradient-to-br from-[#b37b65] via-[#996758] to-[#7e4f46] text-white'
      : 'bg-white border border-gray-100 text-gray-800'
    }
    shadow-lg transition-transform duration-200 hover:-translate-y-0.5
  `}>
    {/* Top row: label + icon */}
    <div className="flex items-start justify-between mb-4">
      <p className={`text-[11px] font-bold uppercase tracking-[0.15em] ${
        accent === 'dark' ? 'text-[#c8a48a]' : accent === 'warm' ? 'text-white/70' : 'text-gray-400'
      }`}>
        {label}
      </p>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${
        accent === 'dark' ? 'bg-white/10' : accent === 'warm' ? 'bg-white/15' : 'bg-gray-50'
      }`}>
        {icon}
      </div>
    </div>

    {/* Value */}
    {loading ? (
      <div className={`h-9 w-24 rounded-lg animate-pulse ${
        accent === 'dark' || accent === 'warm' ? 'bg-white/20' : 'bg-gray-100'
      }`} />
    ) : (
      <p className={`text-3xl sm:text-4xl font-black tracking-tight leading-none`}>{value}</p>
    )}

    {/* Sub-label */}
    {sub && (
      <p className={`mt-2 text-xs ${
        accent === 'dark' ? 'text-gray-500' : accent === 'warm' ? 'text-white/60' : 'text-gray-400'
      }`}>
        {sub}
      </p>
    )}

    {/* Decorative ring */}
    <div className={`absolute -right-6 -bottom-6 w-24 h-24 rounded-full opacity-10 ${
      accent === 'dark' ? 'bg-[#b37b65]' : accent === 'warm' ? 'bg-white' : 'bg-[#b37b65]'
    }`} />
  </div>
);

const ManagerDashboard = () => {
  const user = getStoredUser();
  const branchId = user?.branch_id;
  const managerName = user?.full_name || 'Manager';
  const firstName = managerName.split(' ')[0];

  const [bookings, setBookings] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [timeframe, setTimeframe] = useState('last4');
  const [stats, setStats] = useState({ scheduledToday: 0, cancellationsToday: 0, dailyRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const barColors = ['#b37b65', '#a06b56', '#8e5d4a', '#7e4f46'];

  // Today's date label
  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  useEffect(() => {
    if (!branchId) {
      setError('No branch assigned to your account.');
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE}/get_branch_bookings.php?branch_id=${branchId}`);
        const result = await response.json();
        if (!result.success) {
          setError(result.message || 'Failed to load dashboard data.');
          setBookings([]);
          return;
        }
        setBookings(Array.isArray(result.bookings) ? result.bookings : []);
      } catch (err) {
        console.error(err);
        setError('Unable to load data. Please check your connection.');
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [branchId]);

  useEffect(() => {
    const todayKey = getTodayKey();
    let scheduledToday = 0, cancellationsToday = 0, dailyRevenue = 0;
    bookings.forEach((booking) => {
      const dateKey = getDateKey(booking.appointment_date || booking.created_at);
      if (!dateKey) return;
      const status = normalizeStatus(booking.status);
      const isCancelled = status === 'cancelled' || status === 'canceled';
      if (dateKey === todayKey) {
        if (!isCancelled && status !== 'declined' && ['approved', 'pending', 'completed'].includes(status)) scheduledToday++;
        if (isCancelled) cancellationsToday++;
        if (['approved', 'completed'].includes(status)) {
          const amount = Number(booking.total_price || booking.service_price || 0);
          if (!Number.isNaN(amount)) dailyRevenue += amount;
        }
      }
    });
    setStats({ scheduledToday, cancellationsToday, dailyRevenue });
    setMonthlyData(buildMonthlySeries(bookings, timeframe === 'year' ? 12 : 4));
  }, [bookings, timeframe]);

  return (
    <div className="flex min-h-screen hygea-app-bg">
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <ManagerTopHeader pageTitle="Dashboard" />

        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">

            {/* ── Page Header ── */}
            <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-[#b37b65] uppercase tracking-[0.2em] mb-1">Branch Overview</p>
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">
                  Good {getGreeting()}, {firstName}.
                </h1>
                <p className="text-sm text-gray-400 mt-0.5">{todayLabel}</p>
              </div>
              {/* Quick status pill */}
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-4 py-2 text-xs font-bold self-start sm:self-auto">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Live Data
              </div>
            </div>

            {/* ── Error Banner ── */}
            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <p className="text-sm font-semibold">{error}</p>
              </div>
            )}

            {/* ── Stat Cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-6 sm:mb-8">
              <StatCard
                label="Scheduled Today"
                value={stats.scheduledToday}
                loading={loading}
                accent="default"
                icon="📅"
                sub="Approved · Pending · Completed"
              />
              <StatCard
                label="Cancellations"
                value={stats.cancellationsToday}
                loading={loading}
                accent="dark"
                icon="✕"
                sub="Cancelled today"
              />
              <StatCard
                label="Daily Revenue"
                value={formatCurrency(stats.dailyRevenue)}
                loading={loading}
                accent="warm"
                icon="₱"
                sub="Approved & completed only"
              />
            </div>

            {/* ── Divider label ── */}
            <div className="flex items-center gap-4 mb-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 whitespace-nowrap">Booking Trends</p>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* ── Chart Card ── */}
            <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 p-5 sm:p-6 lg:p-8">
              {/* Chart header */}
              <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3 mb-6 sm:mb-8">
                <div>
                  <h2 className="text-base sm:text-lg font-black text-gray-900 tracking-tight">Monthly Bookings</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Excludes cancelled & declined</p>
                </div>

                {/* Timeframe toggle — pill style */}
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-100 rounded-xl p-1 flex-shrink-0">
                  {[
                    { value: 'last4', label: '4 Months' },
                    { value: 'year', label: '12 Months' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTimeframe(opt.value)}
                      className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-150 ${
                        timeframe === opt.value
                          ? 'bg-[#1c1310] text-white shadow'
                          : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart */}
              <div className="h-56 sm:h-72 w-full">
                {loading ? (
                  <div className="h-full w-full rounded-xl bg-gray-50 animate-pulse" />
                ) : monthlyData.length === 0 ? (
                  <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-gray-400">
                    <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-sm font-medium">No booking data available yet</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData} barCategoryGap="35%">
                      <CartesianGrid strokeDasharray="0" vertical={false} stroke="#f3f4f6" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#9ca3af', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#d1d5db', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}
                        width={28}
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb', radius: 8 }} />
                      <Bar dataKey="value" radius={[8, 8, 4, 4]} maxBarSize={52}>
                        {monthlyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Legend row */}
              {!loading && monthlyData.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-50 flex flex-wrap gap-x-5 gap-y-2">
                  {monthlyData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: barColors[index % barColors.length] }}
                      />
                      <span className="text-xs text-gray-500 font-semibold">{entry.name}</span>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs font-black text-gray-700">{entry.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

// Helper: greeting based on time of day
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export default ManagerDashboard;
